import {
  availableBadges,
  Badge,
  BadgeType,
  carbonReductionBadges,
  climatePositiveBadges,
  sparklyBadges,
  treeBadges,
} from '../badges';
import { CarbonOffset, CarbonOffsetType } from '../carbon-offsets';
import { Country, Currency, isFamilyPlan } from '../locale';
import { isWorkforceSubscription } from '../subscriptions';
import { Tree, TreeType } from '../tree';
import { localProjectIds, LocalTreeCountry } from '../tree-projects';
import { BANK_TRANSFER_SUPPORTED_CURRENCIES } from '../admin';
import { dateParser, toPlural } from '../utils';
import { Tile } from '../forest';
import { getPlanAddonsForBusinessUser } from '.';
import { getClimateStatus, isPublicNameVisible } from './user';
import { CarbonRemoval, CarbonRemovalType } from '../carbon-removal';
import { Registration } from '..';
import { ImpactType } from '../impact';
import type { CertificationLevel } from '@prisma/client';

const AWS_PROFILE_IMAGE_BUCKET = 'offset-earth-user-assets';

export const getUserImageCropped = (picture: string) => {
  const urlChunks = picture.split('/');

  if (urlChunks.length === 0 || !picture.includes(AWS_PROFILE_IMAGE_BUCKET)) {
    return null;
  }

  const fileName = urlChunks.pop();
  return `https://${AWS_PROFILE_IMAGE_BUCKET}.imgix.net/profile-images/${fileName}?w=240&h=240&crop=faces&faces=1&fit=facearea&facepad=2.5`;
};

type GiftingUserType = 'CLIMATE' | 'TREE_PLANTING';

export enum ReferralSourceEnum {
  SEARCH_ENGINE = 'SEARCH_ENGINE',
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  PRESS_ARTICLE = 'PRESS_ARTICLE',
  FRIEND = 'FRIEND',
  ADVERT = 'ADVERT',
  TV = 'TV',
  OTHER = 'OTHER',
  UNKNOWN = 'UNKNOWN',
}

export enum AccountTypeEnum {
  BUSINESS = 'business',
  INDIVIDUAL = 'individual',
  LIKELY_BUSINESS = 'likely_business',
  LIKELY_INDIVIDUAL = 'likely_individual',
}

export type AccountType = `${AccountTypeEnum}`;

export enum ClimateStatus {
  FRIENDLY = 'friendly',
  POSITIVE = 'positive',
}

export class User {
  _id: string;
  userId: string;
  username: string;
  email: string;
  pendingEmail?: string;
  secondaryEmails?: string[];
  groupName: string;
  firstName: string;
  lastName: string;
  city: string;
  country: string; // United Kingdom
  location: Country; // GB, ISO-3166 Alpha 2 code
  picture: string;
  subscription?: SubscriptionType;
  customerNumber: number;
  createdAt: Date;
  isPrivate: boolean;
  isBadgesHiddenFromProfile: boolean;
  isEmailVerificationPending?: boolean;
  isDeleted: boolean;
  carbonOffsets: CarbonOffset[];
  carbonRemovals?: CarbonRemoval[];
  trees: Tree[];
  checklistItems: ChecklistItem[];
  referrerUserId?: string;
  referralSource?: ReferralSourceEnum;
  blurb?: string;
  link?: string;
  billingCompanyName: string;
  billingEmail: string;
  billingAddress1: string;
  billingAddress2: string;
  billingCity: string;
  billingPostcode: string;
  billingCountry: string;
  billingLocation: string;
  apiKeys: APIKeyType[];
  referralImpactIncluded?: ReferralImpactIncludedType;
  certificates: Certificate[];
  forestName?: string;
  achievements: Badge[];
  completedAchievementIds: string[];
  isUserType: boolean;
  giftingUserType?: GiftingUserType;
  hasNameAsCallout?: boolean;
  isPayByInvoiceCustomer?: boolean;
  isChildProfile?: boolean;
  isParentProfile?: boolean;
  parentProfile?: ParentProfile;
  totalTrees: number;
  totalSparklyTrees: number;
  totalTreeTransactions: number;
  totalCarbonTonnes: number;
  totalCarbonRemovedTonnes: number;
  totalHabitatMetersRestored: number;
  projectTreesContributionAggregate: ProjectTreesContributionAggregate[];
  isEmailDenyListed?: boolean;
  isCPWMonthsHidden?: boolean;
  isInvestor?: boolean;
  isMtpUser?: boolean;
  isYearOfFirstImpactEnabled?: boolean;
  apiPoNumber?: string;
  bespokeApiPrices?: BespokeApiPrices;
  tiles: Tile[];
  accountType?: AccountType;
  // V2 --------------------------------
  isV2: boolean;
  isBeingMigratedToV2: boolean;
  customer?: CustomerType;

  constructor(user: UserType) {
    // Parse all the dates
    const subscription = user.subscription;
    if (subscription) {
      subscription.addons = subscription.addons || [];
      subscription.startedAt = dateParser(subscription.startedAt);
      subscription.nextBillingAt = subscription.nextBillingAt ? dateParser(subscription.nextBillingAt) : undefined;
    }

    const carbonOffsets = (user.carbonOffsets || []).map(co => ({
      ...co,
      createdAt: dateParser(co.createdAt),
      impactType: ImpactType.CARBON_OFFSETS,
    }));

    const carbonRemovals = (user.carbonRemovals || []).map(cr => ({
      ...cr,
      createdAt: dateParser(cr.createdAt),
      impactType: ImpactType.CARBON_REMOVAL,
    }));

    const apiKeys = (user.apiKeys || []).map(apiKey => ({
      ...apiKey,
      createdAt: dateParser(apiKey.createdAt),
    }));

    const achievements = (user.badges || []).map(achievement => ({
      ...achievement,
      createdAt: dateParser(achievement.createdAt || ''),
    }));

    const certificates = (user.certificates || [])
      .map(cert => ({
        ...cert,
        issuedAt: dateParser(cert.issuedAt),
      }))
      .sort((a, b) => b.issuedAt.getTime() - a.issuedAt.getTime());

    this._id = user._id;
    this.userId = user.userId;
    this.username = user.username;
    this.email = user.email;
    this.pendingEmail = user.pendingEmail || '';
    this.secondaryEmails = user.secondaryEmails || [];
    this.groupName = user.groupName || '';
    this.firstName = user.firstName || '';
    this.lastName = user.lastName || '';
    this.picture = user.picture || '';
    this.city = user.city || '';
    // ISO 3166 Alpha 2 country code
    this.location = user.location || 'US';
    // Human readable version of `location`
    this.country = user.country || '';
    this.subscription = subscription;
    this.customerNumber = user.customerNumber || 0;
    this.createdAt = dateParser(user.createdAt);
    this.isPrivate = !!user.isPrivate;
    this.isParentProfile = user.isParentProfile;
    this.isBadgesHiddenFromProfile = !!user.isBadgesHiddenFromProfile;
    this.isEmailVerificationPending = user.isEmailVerificationPending;
    this.isDeleted = !!user.isDeleted;
    this.carbonOffsets = carbonOffsets;
    this.carbonRemovals = carbonRemovals;
    this.checklistItems = user.checklistItems || [];
    this.referrerUserId = user.referrerUserId;
    this.blurb = user.blurb;
    this.link = user.link;
    this.billingCompanyName = user.billingCompanyName || user.groupName || '';
    this.billingEmail = user.billingEmail || user.email || '';
    this.billingAddress1 = user.billingAddress1 || '';
    this.billingAddress2 = user.billingAddress2 || '';
    this.billingCity = user.billingCity || '';
    this.billingPostcode = user.billingPostcode || '';
    this.billingCountry = user.billingCountry || user.country || '';
    this.billingLocation = user.billingLocation || user.location || '';
    this.apiKeys = apiKeys;
    this.trees = user.trees ? user.trees.map(t => new Tree(t)) : [];
    this.referralImpactIncluded = user.referralImpactIncluded;
    this.certificates = certificates;
    this.forestName = user.forestName;
    this.achievements = achievements.reduce<Badge[]>((acc, badge) => {
      const found = availableBadges[badge.id];
      if (found) {
        acc.push({
          id: badge.id,
          createdAt: badge.createdAt,
          copy: found.copy,
          title: found.title,
          total: found.total,
          category: found.category,
          visibility: found.visibility,
          standoutTotal: found.standoutTotal,
        });
      }
      return acc;
    }, []);
    this.completedAchievementIds = this.achievements.map(a => a.id);
    this.isUserType = true;
    this.giftingUserType = user.giftingUserType;
    this.hasNameAsCallout = user.hasNameAsCallout;
    this.referralSource = user.referralSource;
    this.isPayByInvoiceCustomer = user.isPayByInvoiceCustomer;
    this.isChildProfile = user.isChildProfile;
    this.parentProfile = user.parentProfile;
    this.totalTrees = user.totalTrees;
    this.totalSparklyTrees = user.totalSparklyTrees ?? 0;
    this.totalTreeTransactions = user.totalTreeTransactions;
    this.totalCarbonTonnes = user.totalCarbonTonnes;
    this.totalCarbonRemovedTonnes = user.totalCarbonRemovedTonnes;
    this.totalHabitatMetersRestored = user.totalHabitatMetersRestored;
    this.projectTreesContributionAggregate = user.projectTreesContributionAggregate;
    this.isEmailDenyListed = !!user.isEmailDenyListed;
    this.isCPWMonthsHidden = !!user.isCPWMonthsHidden;
    this.isInvestor = !!user.isInvestor;
    this.tiles = user.tiles;
    this.isMtpUser = !!user.isMtpUser;
    this.isYearOfFirstImpactEnabled = !!user.isYearOfFirstImpactEnabled;
    this.apiPoNumber = user.apiPoNumber;
    this.bespokeApiPrices = user.bespokeApiPrices;

    this.accountType = user.accountType;

    // V2 User Properties --------------------------------------------
    this.isV2 = user.isV2;
    this.isBeingMigratedToV2 = user.isBeingMigratedToV2;
    this.customer = user.customer;
  }

  get fullNamePlural() {
    return toPlural(this.fullName());
  }

  get firstNamePlural() {
    return toPlural(this.firstName);
  }

  get preferredCurrency(): Currency {
    return (this.customer?.currency || 'GBP') as Currency;
  }

  get niceNamePlural() {
    return this.firstName ? toPlural(this.firstName) : toPlural(this.username);
  }

  getProfileImage(clientUrl: string = '') {
    // Default images are just numbers between 0 and 5
    const isDefaultImage = parseInt(this.picture) > -1;

    return isDefaultImage
      ? `${clientUrl}/images/app/avatars/${this.picture}.png`
      : getUserImageCropped(this.picture) || this.picture;
  }

  getParentProfileImage(clientUrl: string = '') {
    if (this.isChildProfile && this.parentProfile && this.parentProfile.picture) {
      const isDefaultImage = parseInt(this.parentProfile.picture) > -1;

      return isDefaultImage
        ? `${clientUrl}/images/app/avatars/${this.parentProfile.picture}.png`
        : getUserImageCropped(this.parentProfile.picture) || this.parentProfile.picture;
    }

    return '';
  }

  get isFamilyPlan() {
    return this.subscription && this.subscription.planId && isFamilyPlan(this.subscription.planId);
  }

  get isBusinessPlan() {
    return this.subscription && this.subscription.planId && isWorkforceSubscription(this.subscription.planId);
  }

  get climateStatus(): ClimateStatus | null {
    if (!this.subscription) return null;

    return getClimateStatus(
      this.subscription.planId,
      this.subscription.addons.map(a => a.id),
    );
  }

  /**
   * Are they a "business-y" customer. This means a user which
   * has signed up for their business purposes. This excludes users
   * who have signed up via a gift and users which have personal
   * or family subscriptions.
   */
  get isBusinessType() {
    return this.accountType === AccountTypeEnum.BUSINESS || this.accountType === AccountTypeEnum.LIKELY_BUSINESS;
  }

  get isBillingEnabled() {
    return !!this.subscription || this.isPayByInvoiceCustomer || !!this.maskedCardNumber;
  }

  get maskedCardNumber() {
    // V2
    if (this.customer?.maskedCardNumber) {
      return this.customer.maskedCardNumber || '';
    } else if (this.subscription) {
      // V1
      if ('maskedCardNumber' in this.subscription) {
        return this.subscription.maskedCardNumber || '';
      }
    }

    return '';
  }

  hasCard() {
    return this.maskedCardNumber.length > 0;
  }

  get isPaused(): boolean {
    return Boolean(this.subscription && this.subscription.isPaused);
  }

  // Remove the http(s) and www for display
  get linkClean() {
    if (!this.link) {
      return '';
    }

    return this.link.replace(/https?:\/\/(www\.)?/, '');
  }

  fullName() {
    if (!isPublicNameVisible(this.accountType)) {
      return this.groupName.trim() || this.username;
    }

    const fullName = (this.isFamilyPlan ? this.groupName : `${this.firstName} ${this.lastName}`).trim();
    return fullName || `${this.firstName} ${this.lastName}`.trim() || `User ${this.username}`;
  }

  totalNumberOfTonnesOffset() {
    return this.totalCarbonTonnes;
  }

  totalNumberOfCarbonRemoved() {
    return this.totalCarbonRemovedTonnes;
  }

  totalNumberOfTrees() {
    return this.totalTrees;
  }

  totalNumberOfSparkleTrees() {
    return this.totalSparklyTrees;
  }

  totalNumberOfEmployeesForCPW() {
    const cpwAddons = getPlanAddonsForBusinessUser(this);
    return cpwAddons.tier1 + cpwAddons.tier2 + cpwAddons.tier3;
  }

  getMonthsSubscribed() {
    return this.subscription ? this.subscription.timesRenewed : 0;
  }

  findTreeById(treeId: string) {
    return this.trees.find(t => t._id === treeId);
  }

  getLocalProjectAddons() {
    return this.subscription ? this.subscription.addons.filter(a => localProjectIds.includes(a.id)) : [];
  }

  hasAddress() {
    return Boolean(this.billingAddress1 && this.billingCity && this.billingCountry && this.billingPostcode);
  }

  isPBIEligible() {
    return Boolean(this.isBusinessType && BANK_TRANSFER_SUPPORTED_CURRENCIES.includes(this.preferredCurrency));
  }

  isBadgeComplete(badgeId: string) {
    if (badgeId === 'mtpBadge') return true;
    return this.completedAchievementIds.includes(badgeId);
  }

  getTop4Badges() {
    let topBadges = this.achievements;

    // Map to their badge objects, ignoring invalid ids
    topBadges = topBadges.reduce<Badge[]>((acc, badge) => {
      const found = availableBadges[badge.id];
      if (found) acc.push(found);
      return acc;
    }, []);

    if (topBadges.length < 4) {
      // Get more badges to fill the UI
      // Filter func
      const f = (b: Badge) => !this.completedAchievementIds.includes(b.id);

      const lockedTreeBadges = treeBadges.filter(f);
      const nextTreeBadge = lockedTreeBadges.shift();
      if (nextTreeBadge) {
        topBadges.push(nextTreeBadge);
      }

      const lockedCarbonBadges = carbonReductionBadges.filter(f);
      const nextCarbonBadge = lockedCarbonBadges.shift();
      if (nextCarbonBadge) {
        topBadges.push(nextCarbonBadge);
      }

      const lockedSparklyTreeBadges = sparklyBadges.filter(f);
      const nextSparklyTreeBadge = lockedSparklyTreeBadges.shift();
      if (nextSparklyTreeBadge) {
        topBadges.push(nextSparklyTreeBadge);
      }

      if (!this.subscription) {
        const secondTreeBadge = lockedTreeBadges.shift();
        if (secondTreeBadge) {
          topBadges.push(secondTreeBadge);
        }
      } else {
        const lockedClimatePositiveBadges = climatePositiveBadges.filter(f);
        const nextClimatePositiveBadge = lockedClimatePositiveBadges.shift();
        if (nextClimatePositiveBadge) {
          topBadges.push(nextClimatePositiveBadge);
        }
      }

      // return the top 4 achievements
      return topBadges.slice(0, 4);
    }

    // return the latest 4 achievements
    return topBadges.slice(-4, topBadges.length);
  }

  /**
   * Return the path to the user profile for the currently logged in user
   * depending on whether it is for a standalone profile or a sub profile
   */
  getProfilePath() {
    if (this.isChildProfile && this.parentProfile && this.parentProfile.username) {
      return `/${this.parentProfile.username}/${this.username}`;
    } else {
      return `/${this.username}`;
    }
  }

  /**
   * Returns the user profile path for the logged in user with the
   * supplied referral ID
   */
  getProfilePathWithReferralId(referralId: string) {
    return `${this.getProfilePath()}?r=${referralId}`;
  }
}

export type ChecklistItem = {
  _id: string;
  text: string;
  isComplete: boolean;
};

export type SubscriptionType = {
  currencyCode: string;
  customerId: string;
  nextBillingAt: Date | undefined;
  planId: string;
  startedAt: Date;
  timesRenewed: number;
  isFailingPayment?: boolean;
  addons: { id: string; quantity: number }[];
  isPaused?: boolean;
} & (
  | {
      // `null` if not Active | Incomplete
      processorId: string | null;
    }
  | {
      maskedCardNumber: string;
    }
);

export type APIKeyType = {
  key: string;
  description: string;
  createdAt: Date;
};

export type Certificate = {
  invoiceId?: string;
  issuedAt: Date;
  url: string;
  wasRegenerated?: boolean;
};

export type ReferralImpactIncludedType = 'ALL';

export type ParentProfile = {
  userId: string;
  username: string;
  picture?: string;
  forestName?: string;
};

export type BespokeLocalTreePrices = Partial<Record<LocalTreeCountry, number>>;

export type BespokeApiPrices = Record<ImpactType, number> & {
  LOCAL_TREES?: BespokeLocalTreePrices;
};

type BaseUser = {
  _id: string;
  carbonOffsets?: CarbonOffsetType[];
  carbonRemovals?: CarbonRemovalType[];
  trees: TreeType[];
  userId: string;
  username: string;
  email: string;
  pendingEmail: string;
  secondaryEmails?: string[];
  groupName?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  country?: string;
  location: Country;
  picture?: string;
  subscription?: SubscriptionType;
  customerNumber?: number;
  createdAt: Date;
  isPrivate?: boolean;
  giftingUserType?: GiftingUserType;
  isBadgesHiddenFromProfile?: boolean;
  isEmailVerificationPending?: boolean;
  isDeleted?: boolean;
  checklistItems?: ChecklistItem[];
  referrerUserId?: string;
  referralSource?: ReferralSourceEnum;
  blurb?: string;
  link?: string;
  billingCompanyName?: string;
  billingEmail: string;
  billingAddress1?: string;
  billingAddress2?: string;
  billingCity?: string;
  billingPostcode?: string;
  billingCountry?: string;
  billingLocation?: Country;
  apiKeys?: APIKeyType[];
  badges?: BadgeType[];
  certificates?: Certificate[];
  hasNameAsCallout?: boolean;
  forestName?: string;
  achievements?: Badge[];
  isChildProfile?: boolean;
  parentProfile?: ParentProfile;
  isEmailDenyListed?: boolean;
  isCPWMonthsHidden?: boolean;
  isInvestor?: boolean;
  isMtpUser?: boolean;
  isPayByInvoiceCustomer?: boolean;
  isYearOfFirstImpactEnabled?: boolean;
  apiPoNumber?: string;
  bespokeApiPrices?: BespokeApiPrices;
  referralImpactIncluded?: 'ALL';
  isParentProfile?: boolean;
  accountType?: AccountType;
  // V2 ----------------------
  isV2: boolean;
  isBeingMigratedToV2: boolean;
  customer?: CustomerType;
};

export interface UserUpdateFields {
  billingAddress1?: string;
  billingAddress2?: string;
  billingCity?: string;
  billingCompanyName?: string;
  billingCountry?: string;
  billingEmail?: string;
  billingPostcode?: string;
  blurb?: string;
  checklistItems?: ChecklistItem[];
  city?: string;
  country?: string;
  firstName?: string;
  groupName?: string;
  email?: string;
  password?: string;
  isPrivate?: boolean;
  lastName?: string;
  link?: string;
  picture?: string;
  username?: string;
  forestName?: string;
  isBadgesHiddenFromProfile?: boolean;
  apiPoNumber?: string;
}

export type RuntimeCalculatedProperties = {
  totalTrees: number;
  totalSparklyTrees: number;
  totalTreeTransactions: number;
  totalCarbonTonnes: number;
  totalCarbonRemovedTonnes: number;
  totalHabitatMetersRestored: number;
  projectTreesContributionAggregate: ProjectTreesContributionAggregate[];
  tiles: Tile[];
};

export type UserType = BaseUser & RuntimeCalculatedProperties;

export type SalesAgentType = {
  id: number;
  name: string;
  isActive: boolean;
};

export interface CustomerType {
  id: number; // internal db
  processorId: string; // billing provider
  maskedCardNumber?: string;
  currency: Currency;
  certificationLevel?: CertificationLevel;
}

export type UserViewType = BaseUser & {
  isFamilyPlan: boolean;
  isBusinessPlan: boolean;
  fullName: string;
  totalNumberOfTonnesOffset: number;
  totalNumberOfCarbonRemoved: number;
  totalNumberOfTrees: number;
  trees: Tree[];
  profileImage: string;
  linkClean: string;
  isPaused: boolean;
  fullNamePlural: string;
  maskedCardNumber: string;
  groupName: string;
  monthsSubscribed: number;
  isCPWMonthsHidden: boolean;
  top4Badges: Badge[];
  achievements: Badge[];
  firstNamePlural: string;
  niceNamePlural: string;
};

export type ProjectTreesContributionAggregate = {
  projectId: number;
  treeNumber: number;
};

export enum CAPTCHA_ACTIONS {
  PAYG = 'SIGNUP/PAYG',
  INDIVIDUAL_SUB = 'SIGNUP/INDIVIDUAL_SUB',
  BUSINESS_SUB = 'SIGNUP/BUSINESS_SUB',
  SUBSCRIPTION_SETUP_INTENT = 'SUBSCRIPTION/SETUP_INTENT',
  FLIGHTS = 'SIGNUP/IMPACT_SHOP',
}

export const signUpTypeToCaptchaAction: Record<Registration.Type, CAPTCHA_ACTIONS | undefined> = {
  zero: undefined,
  'pay-as-you-go': CAPTCHA_ACTIONS.PAYG,
  individual: CAPTCHA_ACTIONS.INDIVIDUAL_SUB,
  business: CAPTCHA_ACTIONS.BUSINESS_SUB,
  shopify: undefined,
  flights: CAPTCHA_ACTIONS.FLIGHTS,
};
